import dayjs from 'dayjs';
import ReminderService, { REMINDER_FILTER_STATE_TYPES, ActivityType, ReminderPayload, ReminderStateType, ACTIVITY_TYPES, ReminderTagOption } from '../../services/ReminderService';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Card, DatePicker, Drawer, Form, Input, Popconfirm, Select, Space, Table, Tag, Tabs, Segmented, Row, Tooltip, Typography } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { FileImageOutlined, FileTextOutlined, SearchOutlined } from '@ant-design/icons';
import { CHANNEL_META } from '../../components/Reminders/channelMeta';
import UserService from '../../services/UserService';
import CompanyService from '../../services/CompanyService';
import ProposalService from '../../services/ProposalService';
import AuthorizedPersonTable from '../../components/Companies/AuthorizedPersonTable';
import StatusService from '../../modules/Status/Service/StatusService';
import tinycolor from 'tinycolor2';
import { hasRole, openNotification, normalizeTurkish } from '../../utils';
import ReminderDrawer, { ReminderFormValues } from '../../components/Reminders/ReminderDrawer';
import PenIcon from '../../assets/icons/pen.svg';
import TrashIcon from '../../assets/icons/trash.svg';
import CompletionDrawer from '../../components/Notes/CompletionDrawer';
import EntityNotesDrawer from '../../components/Notes/EntityNotesDrawer';
import { ACTIVITY_LABELS } from './constants';
import { STATE_LABELS } from './stateLabels';
import { http } from '../../http-common';
import AccountCell from '../../components/Reminders/AccountCell';
import { getAssigneAccountCellProps } from '../../components/Reminders/accountCellHelper';
import { UserListIcon } from 'hugeicons-react';
import { buildFullReminderPayload } from '../../utils/reminderUtils';
import {
  buildClearedReminderFilters,
  buildReminderInitialStateFromSearchParams,
  getDateRangeForMode,
  type ReminderViewMode,
} from './reminderQueryState';
const { Title } = Typography;

const STATE_COLOR_MAP: Record<ReminderStateType, string> = {
  PENDING: 'orange',
  PROCESSING: 'cyan',
  ACCEPT: 'green',
  REJECT: 'red',
  COMPLETED: 'blue',
    CLOSED: 'gray',
};


type ReminderRecord = {
  id: number;
  content?: string;
  entityName?: string;
  entityId?: string | number;
  reminderDate?: string;
  dueDate?: string;
  estimatedDurationMinutes?: number;
  resolvedAt?: string;
  properties?: Record<string, any> | null;
  activityType?: ActivityType;
  channel?: string;
  link?: string;
  state?: ReminderStateType;
  statusId?: number;
  status?: {
    id?: number;
    name?: string;
    color?: string;
  };
  account?: any;
  accountId?: number;
  createdByAccount?: any;
  createdBy?: string;
  result?: string;
  tags?: Array<{ id?: number; name?: string } | string>;
};

const ALLOWED_ROLES = ['ADMIN', 'CENTRAL_USER', 'FINANCE', 'INSPECTOR'];

const Reminders: React.FC = () => {
  const [searchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const initialRouteState = useMemo(
    () => buildReminderInitialStateFromSearchParams(new URLSearchParams(searchParamsKey)),
    [searchParamsKey]
  );
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ReminderRecord[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100, total: 0 });
  const [filters, setFilters] = useState<any>(initialRouteState.filters);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ReminderViewMode>(initialRouteState.viewMode);
  const [editTarget, setEditTarget] = useState<ReminderRecord | null>(null);
  const [filterForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const remindersRequestRef = useRef<{ controller: AbortController; requestId: number } | null>(null);
  const searchDebounceRef = useRef<number | null>(null);
  const routeParamsAppliedRef = useRef(false);
  const [users, setUsers] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [proposals, setProposals] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [tagOptions, setTagOptions] = useState<ReminderTagOption[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);
  const [drawerValues, setDrawerValues] = useState<Partial<ReminderFormValues>>({
    entityTab: 'COMPANY',
  });
  const DEFAULT_ACTIVITY_TYPE: ActivityType = ACTIVITY_TYPES[0];
  const [completionDrawerVisible, setCompletionDrawerVisible] = useState(false);
  const [completionTarget, setCompletionTarget] = useState<ReminderRecord | null>(null);
  const [completionResult, setCompletionResult] = useState('');
  const [completionSwitchActive, setCompletionSwitchActive] = useState(true);
  const [completionActivityType, setCompletionActivityType] = useState<ActivityType>(DEFAULT_ACTIVITY_TYPE);
  const [completionChannel, setCompletionChannel] = useState<string | undefined>(undefined);
  const [completionReminderDate, setCompletionReminderDate] = useState<dayjs.Dayjs | null>(null);
  const [completionContent, setCompletionContent] = useState('');
  const [completionSubmitting, setCompletionSubmitting] = useState(false);
  const [completionError, setCompletionError] = useState<string | null>(null);
  const [completionResultAttachments, setCompletionResultAttachments] = useState<UploadFile[]>([]);
  const [notesDrawerOpen, setNotesDrawerOpen] = useState(false);
  const [notesDrawerTitle, setNotesDrawerTitle] = useState<string | undefined>(undefined);
  const [notesTargetProposal, setNotesTargetProposal] = useState<any | null>(null);
  const [authorizedPersonsDrawerOpen, setAuthorizedPersonsDrawerOpen] = useState(false);
  const [authorizedPersonsCompanyId, setAuthorizedPersonsCompanyId] = useState<number | null>(null);
  const [authorizedPersonsProposalId, setAuthorizedPersonsProposalId] = useState<number | null>(null);
  const [selectedAuthorizedPersonIds, setSelectedAuthorizedPersonIds] = useState<number[]>([]);
  const userId = localStorage.getItem('id');
  const filterEntityTab = filters.entityTab || 'COMPANY';
  const estimatedDurationSpecified = filters['estimatedDurationMinutes.specified'];
  const filterFormInitialValues = useMemo(
    () => ({
      stateList: filters.stateList,
      filterStatusId: filters.statusId,
      activityType: filters.activityType,
      filterAccountId: filters.accountId,
      filterTagIds: filters.filterTagIds,
      estimatedDurationSpecified,
      entityTab: filterEntityTab,
      filterCompanyId: filterEntityTab === 'COMPANY' ? filters.entityId : undefined,
      filterProposalId: filterEntityTab === 'PROPOSAL' ? filters.entityId : undefined,
      filterProposalCompanyId: filters.proposalCompanyIdFilter,
    }),
    [
      filters.accountId,
      filters.activityType,
      filters.entityId,
      filters.filterTagIds,
      filters.proposalCompanyIdFilter,
      filters.stateList,
      filters.statusId,
      estimatedDurationSpecified,
      filterEntityTab,
    ]
  );

  const isAdmin = useMemo(() => hasRole('ADMIN'), []);
  const isCentralUser = useMemo(() => hasRole('CENTRAL_USER'), []);
  const canManageProposalAuthorizedPersons = useMemo(
    () => isAdmin || isCentralUser || hasRole('FINANCE'),
    [isAdmin, isCentralUser]
  );
  const canCreateReminder = isAdmin || isCentralUser;
  const canCompleteOwnReminder = useMemo(() => {
    if (!editTarget || !userId) return false;
    const ownerId = editTarget.accountId ?? editTarget?.account?.id;
    if (ownerId === undefined || ownerId === null) return false;
    return String(ownerId) === String(userId);
  }, [editTarget, userId]);
  const getStatusNameById = (statusId?: number | string) => {
    if (statusId === undefined || statusId === null) return undefined;
    const status = statuses.find((s) => String(s.id) === String(statusId));
    return status?.name;
  };

  const getResultValue = (record: any) => {
    if (!record) return undefined;
    if (record.result) return record.result;
    const raw = record.properties;
    if (!raw) return undefined;
    if (typeof raw === 'object') return (raw as any)?.result;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return parsed?.result;
      } catch {
        return undefined;
      }
    }
    return undefined;
  };

  const normalizeTagNames = (rawTags: any): string[] => {
    if (!Array.isArray(rawTags)) return [];
    const seen = new Set<string>();
    const names: string[] = [];
    rawTags.forEach((item: any) => {
      const rawName =
        typeof item === 'string'
          ? item
          : typeof item?.name === 'string'
            ? item.name
            : '';
      const normalized = rawName.trim().replace(/\s+/g, ' ');
      if (!normalized) return;
      const key = normalized.toLocaleLowerCase('tr-TR');
      if (seen.has(key)) return;
      seen.add(key);
      names.push(normalized);
    });
    return names;
  };

  const getReminderTags = (record: ReminderRecord): string[] =>
    normalizeTagNames((record as any)?.tags);

  const resolveProposalEntity = (record: ReminderRecord) => {
    const embeddedProposal: any = (record as any).proposal;
    if (embeddedProposal) return embeddedProposal;
    if ((record.entityName || '').toString().toUpperCase() === 'PROPOSAL') {
      return proposals.find((p: any) => String(p.id) === String(record.entityId));
    }
    return undefined;
  };

  const isProposalReminder = (record: ReminderRecord) =>
    (record.entityName || '').toString().toUpperCase() === 'PROPOSAL' && record.entityId !== undefined && record.entityId !== null;

  const getStatusColorById = (statusId?: number | string | null) => {
    if (statusId === undefined || statusId === null) return undefined;
    const status = statuses.find((s: any) => String(s.id) === String(statusId));
    return status?.color;
  };

  const openNotesDrawer = (record: ReminderRecord) => {
    if (!isProposalReminder(record)) return;
    const resolvedProposal = resolveProposalEntity(record) || {
      id: record.entityId,
      statusId: record.statusId ?? record.status?.id,
      status: record.status,
    };
    const proposalLabel =
      resolvedProposal?.proposalNumber ||
      resolvedProposal?.proposalCode ||
      resolvedProposal?.proposalNo ||
      resolvedProposal?.id ||
      record.entityId;
    setNotesTargetProposal(resolvedProposal);
    setNotesDrawerTitle(proposalLabel ? `İşlem Geçmişi - ${proposalLabel}` : 'İşlem Geçmişi');
    setNotesDrawerOpen(true);
  };

  const closeNotesDrawer = () => {
    setNotesDrawerOpen(false);
    setNotesTargetProposal(null);
    setNotesDrawerTitle(undefined);
  };

  const openAuthorizedPersonsDrawer = async (record: ReminderRecord) => {
    if (!canManageProposalAuthorizedPersons || !isProposalReminder(record)) return;

    const proposalId = Number(record.entityId);
    if (!Number.isFinite(proposalId) || proposalId <= 0) return;

    try {
      const response: any = await ProposalService.get(proposalId);
      const proposal = response?.data ?? response;
      const companyId = Number(proposal?.company?.id);

      if (!Number.isFinite(companyId) || companyId <= 0) {
        openNotification('error', 'Hata', 'Yetkili kişi bilgileri alınamadı.');
        return;
      }

      const authorizedPersonIds = Array.isArray(proposal?.authorizedPersonIds)
        ? proposal.authorizedPersonIds
          .map((id: unknown) => Number(id))
          .filter((id: number) => Number.isFinite(id) && id > 0)
        : [];

      setAuthorizedPersonsCompanyId(companyId);
      setAuthorizedPersonsProposalId(proposalId);
      setSelectedAuthorizedPersonIds(authorizedPersonIds);
      setAuthorizedPersonsDrawerOpen(true);
    } catch (error) {
      console.error('Yetkili kişi verileri alınamadı', error);
      openNotification('error', 'Hata', 'Yetkili kişi bilgileri alınamadı.');
    }
  };

  const closeAuthorizedPersonsDrawer = () => {
    setAuthorizedPersonsDrawerOpen(false);
    setAuthorizedPersonsCompanyId(null);
    setAuthorizedPersonsProposalId(null);
    setSelectedAuthorizedPersonIds([]);
  };

  const handleSaveAuthorizedPersons = async () => {
    if (!authorizedPersonsProposalId) return;

    try {
      await ProposalService.updateAuthorizedPersons(authorizedPersonsProposalId, {
        authorizedPersonIds: selectedAuthorizedPersonIds,
      });
      openNotification('success', 'Başarılı', 'Yetkili kişiler kaydedildi.');
      closeAuthorizedPersonsDrawer();
      await fetchReminders();
    } catch (error) {
      console.error('Yetkili kişiler kaydedilemedi', error);
      openNotification('error', 'Hata', 'Yetkili kişiler kaydedilemedi.');
    }
  };

  const renderCompanyCell = (record: ReminderRecord) => {
    const embeddedProposal: any = (record as any).proposal;
    const embeddedCompany: any = (record as any).company || embeddedProposal?.company;
    if (embeddedCompany) {
      return embeddedCompany.name || embeddedCompany.title || '-';
    }
    if ((record.entityName || '').toString().toUpperCase() === 'COMPANY') {
      const company = companies.find((c: any) => String(c.id) === String(record.entityId));
      if (company) {
        return company.name || company.title || `Firma #${record.entityId}`;
      }
      return `Firma #${record.entityId || '-'}`;
    }
    return '-';
  };

  const renderProposalCell = (record: ReminderRecord) => {
    const resolvedProposal = resolveProposalEntity(record);
    if (!resolvedProposal) return '-';
    const proposalNumberValue = resolvedProposal.proposalNumber;
    const fallbackCode =
      resolvedProposal.proposalNo ||
      resolvedProposal.proposalCode ||
      `Teklif #${resolvedProposal.id}`;
    const createdAt = resolvedProposal.createdAt ? dayjs(resolvedProposal.createdAt).format('DD/MM/YYYY') : undefined;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: createdAt ? 2 : 0 }}>
        {proposalNumberValue ? (
          <Link to={`/proposals?search=${encodeURIComponent(proposalNumberValue)}`} style={{ color: '#2563eb', fontWeight: 500 }}>
            {proposalNumberValue}
          </Link>
        ) : (
          <span>{fallbackCode}</span>
        )}
        {createdAt && (
          <span style={{ fontSize: 12, color: '#6b7280' }}>{createdAt}</span>
        )}
      </div>
    );
  };

  const resolveProposalReminderLimit = (proposal?: any) => {
    if (!proposal) return undefined;
    const statusId = proposal.statusId ?? proposal.status?.id;
    const statusName =
      proposal.status?.name ||
      proposal.statusName ||
      proposal.statusTitle ||
      getStatusNameById(statusId);
    const normalizedStatus = normalizeTurkish(String(statusName || '')).toLowerCase();
    if (statusId === 3 || normalizedStatus.includes('teklif gonderildi') || normalizedStatus.includes('teklif gönderildi')) {
      return { maxDate: dayjs().add(30, 'day').endOf('day'), limitDays: 30 };
    }
    if (statusId === 12 || normalizedStatus.includes('manuel takip')) {
      return { maxDate: dayjs().add(60, 'day').endOf('day'), limitDays: 60 };
    }
    return undefined;
  };

  const getReminderLimitForValues = (values: ReminderFormValues) => {
    if ((values.entityTab || 'COMPANY') !== 'PROPOSAL') return undefined;
    const proposalId = values.proposalId;
    const proposalFromList = proposalId
      ? proposals.find((p: any) => String(p.id) === String(proposalId))
      : undefined;
    const embeddedProposal = (editTarget as any)?.proposal;
    return resolveProposalReminderLimit(proposalFromList || embeddedProposal);
  };

  const renderReminderAccountCell = (record: ReminderRecord) => {
    const creatorProps = getAssigneAccountCellProps(record);
    return <AccountCell {...creatorProps} />;
  };

  const renderCreatedByCell = (record: ReminderRecord) => {
    const createdAccount: any = (record as any)?.createdByAccount || (record as any)?.createdByUser || (record as any)?.createdByInfo;
    if (createdAccount) {
      const fullName = `${createdAccount.firstName ?? ''} ${createdAccount.lastName ?? ''}`.trim()
        || createdAccount.email
        || createdAccount.id
        || '-';
      return (
        <div style={{ lineHeight: 1.2 }}>
          <div style={{ fontWeight: 600 }}>{fullName}</div>
          {createdAccount.email && <div style={{ color: '#6b7280', fontSize: 12 }}>{createdAccount.email}</div>}
        </div>
      );
    }
    return <span>Sistem</span>;
  };

  const renderReminderChannelIcon = (channel?: string) => {
    if (!channel) return null;
    const meta = CHANNEL_META[channel];
    if (!meta) return null;
    return (
      <Tooltip title={meta.label}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            fontSize: 16,
            color: '#374151',
          }}
        >
          {meta.icon}
        </span>
      </Tooltip>
    );
  };

  const normalizeChannelValue = (channel?: string) => {
    if (!channel) return channel;
    if (channel === 'OFFLINE' || channel === 'LOCATION') return 'IN_PERSON';
    if (channel === 'MEET') return 'ONLINE';
    if (channel === 'DOCUMENT') return 'DOCUMENT_RECORD';
    if (channel === 'SYSTEM') return 'SOFTWARE_PORTAL_FORM';
    return channel;
  };

  const DOCUMENT_MIME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ];
  const DOCUMENT_EXTS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.ppt', '.pptx'];
  const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];

  const extractExtension = (name: string) => {
    const clean = (name || '').split('?')[0].toLowerCase();
    const dotIndex = clean.lastIndexOf('.');
    return dotIndex >= 0 ? clean.slice(dotIndex) : '';
  };

  const resolveFileName = (file: Partial<UploadFile> | File) => {
    if ('name' in file && file.name) return file.name;
    if ('url' in file && typeof file.url === 'string') {
      const parts = file.url.split('/');
      return parts[parts.length - 1] || '';
    }
    return '';
  };

  const isImageFile = (file: Partial<UploadFile> | File) => {
    const type = 'type' in file ? file.type || '' : '';
    if (type.startsWith('image/')) return true;
    const ext = extractExtension(resolveFileName(file));
    return IMAGE_EXTS.includes(ext);
  };

  const isDocumentFile = (file: Partial<UploadFile> | File) => {
    const type = 'type' in file ? file.type || '' : '';
    if (DOCUMENT_MIME_TYPES.some((t) => type === t || type.startsWith(t))) return true;
    const ext = extractExtension(resolveFileName(file));
    return DOCUMENT_EXTS.includes(ext);
  };

  const splitAttachmentLists = (fileList: UploadFile[]) => {
    const images: UploadFile[] = [];
    const documents: UploadFile[] = [];
    fileList.forEach((file) => {
      if (isImageFile(file)) {
        images.push(file);
      } else if (isDocumentFile(file)) {
        documents.push(file);
      }
    });
    return { images, documents };
  };

  const buildUploadList = (urls?: string[], prefix: string = 'file') => {
    if (!Array.isArray(urls)) return [];
    return urls
      .filter((u) => typeof u === 'string' && u.length)
      .map((url, idx) => ({
        uid: `${prefix}-${idx}-${url}`,
        name: url.split('/').pop() || `file-${idx + 1}`,
        url,
        status: 'done' as const,
        type: prefix.includes('image') ? 'image/*' : 'application/octet-stream',
      }));
  };

  const parseResultAttachments = (raw?: any) => {
    if (!raw) return [];
    let parsed: any = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch {
        return [];
      }
    }
    const resultImages = Array.isArray(parsed?.resultImages) ? parsed.resultImages : [];
    const resultDocuments = Array.isArray(parsed?.resultDocuments) ? parsed.resultDocuments : [];
    return [
      ...buildUploadList(resultImages, 'result-image'),
      ...buildUploadList(resultDocuments, 'result-document'),
    ];
  };

  const isOwnedPendingReminder = (record: ReminderRecord) => {
    const ownerId = record.accountId ?? record?.account?.id;
    if (!ownerId || !userId) return false;
    return !isAdmin && String(ownerId) === String(userId) && record.state === 'PENDING';
  };

  const renderReminderStateTag = (state?: ReminderStateType) => {
    if (!state) return null;
    return <Tag color={STATE_COLOR_MAP[state] || 'default'}>{STATE_LABELS[state] || state}</Tag>;
  };

  const renderReminderStatusBadge = (status?: ReminderRecord['status']) => {
    const statusName = status?.name;
    if (!statusName) return null;
    const baseColor = status?.color || '#d1d5db';
    const color = tinycolor(baseColor);
    const transparentColor = color.setAlpha(0.1).toRgbString();
    const transparentBorder = color.setAlpha(0.5).toRgbString();

    return (
      <Tag
        style={{
          color: baseColor,
          backgroundColor: transparentColor,
          borderColor: transparentBorder,
          borderRadius: '16px',
          padding: '4px 10px',
          fontSize: '13px',
          display: 'inline-block',
          minWidth: '100px',
          textAlign: 'center',
          fontWeight: 600,
        }}
      >
        {statusName}
      </Tag>
    );
  };

  const extractFileName = (url: string) => {
    if (!url) return '';
    const clean = url.split('?')[0];
    const parts = clean.split('/');
    return parts[parts.length - 1] || clean;
  };

  const isImageUrl = (url: string) => {
    const name = extractFileName(url).toLowerCase();
    return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'].some((ext) => name.endsWith(ext));
  };

  const getReminderAttachments = (record: ReminderRecord) => {
    const raw = record.properties;
    if (!raw) return [];
    let parsed: any = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch {
        return [];
      }
    }
    const images = Array.isArray(parsed?.images) ? parsed.images : [];
    const documents = Array.isArray(parsed?.documents) ? parsed.documents : [];
    const resultImages = Array.isArray(parsed?.resultImages) ? parsed.resultImages : [];
    const resultDocuments = Array.isArray(parsed?.resultDocuments) ? parsed.resultDocuments : [];
    return [...images, ...documents, ...resultImages, ...resultDocuments]
      .filter((url) => typeof url === 'string' && url.length)
      .map((url: string) => {
        const filename = extractFileName(url) || 'Ek';
        const isImage = isImageUrl(url);
        return {
          url,
          filename,
          type: isImage ? 'image' : 'document',
        };
      });
  };

  useEffect(() => {
    loadUsers();
    loadCompanies();
    loadProposals();
    loadStatuses();
    loadTags();
  }, []);

  useEffect(() => {
    if (!routeParamsAppliedRef.current) {
      routeParamsAppliedRef.current = true;
      return;
    }

    setViewMode(initialRouteState.viewMode);
    setFilters(initialRouteState.filters);
    setSearchText('');
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [initialRouteState, searchParamsKey]);

  useEffect(() => {
    filterForm.setFieldsValue(filterFormInitialValues);
  }, [filterForm, filterFormInitialValues]);

  useEffect(() => {
    fetchReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, filters, isAdmin]);

  useEffect(() => () => {
    remindersRequestRef.current?.controller.abort();
  }, []);

  const loadUsers = async () => {
    try {
      const resp: any = await UserService.getUsersWithoutPagination({});
      const list: any[] = resp?.data?.content ?? resp?.data ?? [];
      const filtered = list.filter((u) => {
        const roleNames = (u?.roles ?? []).map((r: any) => r?.name).filter(Boolean);
        return roleNames.some((name: string) => ALLOWED_ROLES.includes(name));
      }).sort((a, b) => {
        const nameA = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim().toLocaleLowerCase('tr-TR');
        const nameB = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim().toLocaleLowerCase('tr-TR');
        return nameA.localeCompare(nameB, 'tr-TR');
      });
      setUsers(filtered);
    } catch (e) {
      console.error('Users could not be loaded', e);
    }
  };

  const loadCompanies = async () => {
    try {
      const resp: any = await CompanyService.getCompanyAll();
      const list: any[] = resp?.data ?? resp ?? [];
      setCompanies(list);
    } catch (e) {
      console.error('Companies could not be loaded', e);
    }
  };

  const loadProposals = async () => {
    try {
      const resp: any = await ProposalService.getAllWithoutPagination({});
      const list: any[] = resp?.data ?? resp ?? [];
      setProposals(list);
    } catch (e) {
      console.error('Proposals could not be loaded', e);
    }
  };

  const loadStatuses = async () => {
    try {
      const resp: any = await StatusService.getAll();
      const list: any[] = resp?.data ?? resp ?? [];
      setStatuses(list);
    } catch (e) {
      console.error('Statuses could not be loaded', e);
    }
  };

  const loadTags = async (search?: string) => {
    setLoadingTags(true);
    setTagError(null);
    try {
      const resp: any = await ReminderService.listReminderTags(search?.trim() || undefined);
      const list = resp?.data ?? resp ?? [];
      setTagOptions(Array.isArray(list) ? list : []);
    } catch (e) {
      setTagError('Etiketler yüklenemedi.');
      console.error('Tags could not be loaded', e);
    } finally {
      setLoadingTags(false);
    }
  };

  const fetchReminders = async () => {
    const previousRequest = remindersRequestRef.current;
    previousRequest?.controller.abort();
    const controller = new AbortController();
    const requestId = (previousRequest?.requestId ?? 0) + 1;
    remindersRequestRef.current = { controller, requestId };
    setLoading(true);
    try {
    const { entityNames, stateList, statusId, entityId, filterTagIds, ...restFilters } = filters;
    const hasEntityFilter = entityId !== undefined && entityId !== null && entityId !== '';
    const requestFilters: Record<string, any> = { ...restFilters };
    if (!hasEntityFilter) {
      delete requestFilters.entityTab;
    }
    const params = {
      ...requestFilters,
      ...(hasEntityFilter && Array.isArray(entityNames) ? { entityNames: entityNames.join(',') } : {}),
      ...(hasEntityFilter ? { entityId } : {}),
      ...(Array.isArray(stateList) && stateList.length ? { state: stateList } : {}),
      ...(Array.isArray(filterTagIds) && filterTagIds.length ? { 'tagId.in': filterTagIds } : {}),
      ...(statusId ? { statusId } : {}),
      sort: 'reminderDate,ASC',
      page: (pagination.current || 1) - 1,
      size: pagination.pageSize || 10,
    };
      const resp: any = isAdmin
        ? await ReminderService.getAdminReminders(params, { signal: controller.signal })
        : await ReminderService.getMyReminders(params, { signal: controller.signal });
      if (remindersRequestRef.current?.requestId !== requestId || controller.signal.aborted) {
        return;
      }
      const payload = resp?.data ?? resp ?? {};
      const listRaw: ReminderRecord[] = (payload?.content ?? payload ?? []) as any;
      const list: ReminderRecord[] = Array.isArray(listRaw) ? listRaw : [];
      const total = payload?.totalElements ?? payload?.total ?? list.length;
      setData(list);
      setPagination((prev) => ({ ...prev, total: total || list.length }));
    } catch (e: any) {
      if (
        controller.signal.aborted ||
        e?.code === 'ERR_CANCELED' ||
        e?.message === 'canceled' ||
        e?.message === 'Operation canceled by the user.'
      ) {
        return;
      }
      openNotification('error', 'Hata', e?.response?.data || 'Görevler yüklenemedi');
    } finally {
      if (remindersRequestRef.current?.requestId === requestId) {
        remindersRequestRef.current = null;
        setLoading(false);
      }
    }
  };

  const handleTableChange = (next: any) => {
    setPagination((prev) => ({
      ...prev,
      current: next.current || 1,
      pageSize: next.pageSize || prev.pageSize,
    }));
  };

  const applyViewMode = (mode: ReminderViewMode) => {
    setViewMode(mode);
    const { startDate, endDate } = getDateRangeForMode(mode);
    setFilters((prev: any) => ({ ...prev, startDate, endDate }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleFilter = async () => {
    const values = await filterForm.validateFields();
    const range = values.dateRange || [];
    const tab = (values.entityTab || filters?.entityTab || 'COMPANY') as 'COMPANY' | 'PROPOSAL';
    const entityId =
      tab === 'COMPANY'
        ? values.filterCompanyId
        : tab === 'PROPOSAL'
          ? values.filterProposalId
          : values.entityId;
    const isAllMode = viewMode === 'ALL';
    const currentStartDate = filters?.startDate;
    const currentEndDate = filters?.endDate;

    setFilters({
      stateList: values.stateList,
      filterTagIds: values.filterTagIds,
      statusId: values.filterStatusId,
      activityType: values.activityType,
      accountId: values.filterAccountId,
      'estimatedDurationMinutes.specified': values.estimatedDurationSpecified,
      search: searchText.trim() ? searchText.trim() : undefined,
      entityTab: tab,
      entityNames: tab === 'COMPANY' ? ['COMPANY'] : tab === 'PROPOSAL' ? ['PROPOSAL'] : undefined,
      entityId,
      proposalCompanyIdFilter: values.filterProposalCompanyId,
      startDate: isAllMode
        ? range[0]
          ? range[0].startOf('day').format('YYYY-MM-DDTHH:mm:ss')
          : undefined
        : currentStartDate,
      endDate: isAllMode
        ? range[1]
          ? range[1].endOf('day').format('YYYY-MM-DDTHH:mm:ss')
          : undefined
        : currentEndDate,
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilterDrawerOpen(false);
  };

  const handleClearFilters = () => {
    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    const clearedFilters = buildClearedReminderFilters(viewMode);
    setSearchText('');
    setFilters(clearedFilters);
    filterForm.resetFields();
    filterForm.setFieldsValue({
      stateList: undefined,
      filterStatusId: undefined,
      activityType: undefined,
      filterAccountId: undefined,
      filterTagIds: undefined,
      estimatedDurationSpecified: undefined,
      entityTab: 'COMPANY',
      filterCompanyId: undefined,
      filterProposalCompanyId: undefined,
      filterProposalId: undefined,
      dateRange: undefined,
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value || '';
    setSearchText(value);
  };

  useEffect(() => {
    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = window.setTimeout(() => {
      const trimmed = searchText.trim();
      setFilters((prev: any) => ({
        ...prev,
        search: trimmed ? trimmed : undefined,
      }));
      setPagination((prev) => ({ ...prev, current: 1 }));
    }, 350);
    return () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchText]);

  const handleOpenCreate = () => {
    setEditTarget(null);
    setDrawerValues({ entityTab: 'COMPANY' });
    setDrawerOpen(true);
  };

  const openCompletionDrawer = (record: ReminderRecord) => {
    setCompletionTarget(record);
    setCompletionResult(record.result ?? '');
    setCompletionSwitchActive(true);
    setCompletionActivityType(record.activityType ?? DEFAULT_ACTIVITY_TYPE);
    setCompletionChannel(normalizeChannelValue(record.channel));
    setCompletionReminderDate(null);
    setCompletionContent(record.content ?? '');
    setCompletionResultAttachments(parseResultAttachments(record.properties));
    setCompletionError(null);
    setCompletionDrawerVisible(true);
  };

  const closeCompletionDrawer = () => {
    setCompletionDrawerVisible(false);
    setCompletionTarget(null);
    setCompletionResult('');
    setCompletionSwitchActive(true);
    setCompletionActivityType(DEFAULT_ACTIVITY_TYPE);
    setCompletionChannel(undefined);
    setCompletionReminderDate(null);
    setCompletionContent('');
    setCompletionError(null);
    setCompletionResultAttachments([]);
  };

  const resolveCompletionStatusId = async (target: ReminderRecord) => {
    const fallbackStatusId =
      target.statusId ??
      (target.status && typeof target.status === 'object' ? target.status.id : undefined);
    const isProposalEntity = (target.entityName || '').toString().toUpperCase() === 'PROPOSAL';
    const proposalId = Number(target.entityId);

    if (!isProposalEntity || Number.isNaN(proposalId)) {
      return fallbackStatusId;
    }

    try {
      const response: any = await ProposalService.get(proposalId);
      const proposal = response?.data ?? response;
      return proposal?.statusId ?? proposal?.status?.id ?? fallbackStatusId;
    } catch (error) {
      console.error('Proposal status could not be fetched for completion flow', error);
      return fallbackStatusId;
    }
  };

  const handleCompletionSubmit = async () => {
    if (!completionTarget) return;
    if (!completionResult.trim()) {
      setCompletionError('Sonuç alanı boş bırakılamaz.');
      return;
    }
    if (completionSwitchActive) {
      if (!completionActivityType) {
        setCompletionError('Yeni görev için iş adımı seçmelisiniz.');
        return;
      }
      if (!completionChannel) {
        setCompletionError('Yeni görev için iletişim yöntemi seçmelisiniz.');
        return;
      }
      if (!completionReminderDate) {
        setCompletionError('Yeni görev için tarih seçmelisiniz.');
        return;
      }
      if (!completionContent.trim()) {
        setCompletionError('Yeni görev içeriği boş olamaz.');
        return;
      }
    }

    setCompletionSubmitting(true);
    try {
      const uploadFileList = async (fileList?: UploadFile[], code: string = 'reminder-result-file') => {
        if (!fileList || !fileList.length) return [];
        const results: string[] = [];
        for (const file of fileList) {
          if (file.url) {
            results.push(file.url);
            continue;
          }
          if (file.originFileObj) {
            const fd = new FormData();
            fd.append('code', code);
            fd.append('document', file.originFileObj);
            const resp = await http.post('/documents', fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            const data = resp?.data ?? resp;
            const url = data?.documentUrl || data?.filePath || data?.url || data;
            if (url) results.push(url);
          }
        }
        return results;
      };

      const { images: resultImageFiles, documents: resultDocumentFiles } = splitAttachmentLists(completionResultAttachments);
      const [resultImageUrls, resultDocumentUrls] = await Promise.all([
        uploadFileList(resultImageFiles, 'reminder-result-image'),
        uploadFileList(resultDocumentFiles, 'reminder-result-file'),
      ]);

      let baseProps: any = {};
      if (completionTarget?.properties) {
        try {
          baseProps =
            typeof completionTarget.properties === 'string'
              ? JSON.parse(completionTarget.properties)
              : completionTarget.properties;
        } catch {
          baseProps = {};
        }
      }
      const mergedProps = {
        ...(baseProps || {}),
        ...(resultImageUrls.length
          ? { resultImages: resultImageUrls }
          : Array.isArray(baseProps?.resultImages)
            ? { resultImages: baseProps.resultImages }
            : {}),
        ...(resultDocumentUrls.length
          ? { resultDocuments: resultDocumentUrls }
          : Array.isArray(baseProps?.resultDocuments)
            ? { resultDocuments: baseProps.resultDocuments }
            : {}),
      };
      const properties =
        mergedProps && Object.keys(mergedProps).length ? JSON.stringify(mergedProps) : undefined;

      const completionPayload = buildFullReminderPayload(completionTarget, {
        state: 'COMPLETED',
        result: completionResult.trim(),
        ...(properties ? { properties } : {}),
      });

      await ReminderService.updateReminder(completionTarget.id, completionPayload);

      let successMessage = 'Görev tamamlandı.';

      if (completionSwitchActive && completionReminderDate) {
        const nextStatusId = await resolveCompletionStatusId(completionTarget);
        await ReminderService.createForMe({
          entityName: completionTarget.entityName,
          entityId: completionTarget.entityId,
          activityType: completionActivityType,
          channel: completionChannel,
          reminderDate: completionReminderDate.format('YYYY-MM-DDTHH:mm:ss'),
          content: completionContent.trim(),
          estimatedDurationMinutes: completionTarget.estimatedDurationMinutes,
          ...(nextStatusId ? { statusId: nextStatusId } : {}),
        });
        successMessage = 'Görev tamamlandı ve yeni görev oluşturuldu.';
      }

      openNotification('success', 'Başarılı', successMessage);
      closeCompletionDrawer();
      fetchReminders();
    } catch (error) {
      openNotification('error', 'Hata', 'Görev tamamlama sırasında bir hata oluştu.');
      console.error(error);
    } finally {
      setCompletionSubmitting(false);
    }
  };

  const buildDrawerValues = (record: ReminderRecord, overrideState?: ReminderStateType) => {
    const tabKey = record.entityName?.toUpperCase() === 'PROPOSAL' ? 'PROPOSAL' : 'COMPANY';
    const embeddedProposal: any = (record as any)?.proposal;
    const embeddedCompany: any = (record as any)?.company || embeddedProposal?.company;
    const matched = embeddedProposal || proposals.find((p: any) => String(p.id) === String(record.entityId));
    const compId = embeddedCompany?.id ?? matched?.companyId ?? matched?.company?.id;
    const entityIdNumber =
      record.entityId !== undefined && record.entityId !== null && !Number.isNaN(Number(record.entityId))
        ? Number(record.entityId)
        : undefined;
    setDrawerValues({
      accountId: record.accountId ?? record?.account?.id,
      content: record.content,
      companyId: tabKey === 'COMPANY' ? embeddedCompany?.id ?? entityIdNumber : undefined,
      proposalId: tabKey === 'PROPOSAL' ? embeddedProposal?.id ?? entityIdNumber : undefined,
      proposalCompanyId: tabKey === 'PROPOSAL' ? compId : undefined,
      reminderDate: record.reminderDate ? dayjs(record.reminderDate) : undefined,
      dueDate: (record as any).dueDate ? dayjs((record as any).dueDate) : undefined,
      estimatedDurationMinutes: (record as any).estimatedDurationMinutes,
      activityType: record.activityType,
      channel: record.channel,
      link: record.link,
      state:
        overrideState ??
        (record.state as ReminderStateType) ??
        (typeof record.status === 'string' ? record.status : undefined),
      statusId:
        record.statusId ??
        (record.status && typeof record.status === 'object' ? record.status.id : undefined),
      result: getResultValue(record),
      tags: getReminderTags(record),
      properties: record.properties ?? undefined,
      entityTab: tabKey as 'COMPANY' | 'PROPOSAL',
      duplicateOnSave: record.state === 'COMPLETED',
    });
  };

  const handleComplete = (record: ReminderRecord) => {
    openCompletionDrawer(record);
  };

  const handleEdit = (record: ReminderRecord) => {
    if (!isAdmin) return;
    const tabKey = record.entityName?.toUpperCase() === 'PROPOSAL' ? 'PROPOSAL' : 'COMPANY';
    if (tabKey === 'PROPOSAL') {
      if (!proposals.some((p: any) => String(p.id) === String(record.entityId))) {
        const embeddedProposal = (record as any).proposal;
        if (embeddedProposal) {
          setProposals((prev) => [...prev, embeddedProposal]);
        }
      }
    }
    buildDrawerValues(record);
    setEditTarget(record);
    setDrawerOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await ReminderService.remove(id);
      openNotification('success', 'Başarılı', 'Görev silindi');
      fetchReminders();
    } catch (e: any) {
      openNotification('error', 'Hata', e?.response?.data || 'Görev silinemedi');
    }
  };

  const handleDrawerSubmit = async (values: ReminderFormValues) => {
    try {
      const entityName: 'COMPANY' | 'PROPOSAL' = values.entityTab;
      const entityId = entityName === 'COMPANY' ? values.companyId : values.proposalId;
      const isMeeting = values.activityType === 'MEETING_OFFLINE' || values.activityType === 'MEETING_ONLINE';
      const channelDefault = values.activityType === 'MEETING_OFFLINE' ? 'IN_PERSON' : values.activityType === 'MEETING_ONLINE' ? 'ONLINE' : '';
      const reminderDate = values.reminderDate
        ? values.reminderDate.format('YYYY-MM-DDTHH:mm:ss')
        : undefined;
      const dueDate = values.dueDate ? values.dueDate.format('YYYY-MM-DDTHH:mm:ss') : undefined;
      const resolvedChannel = values.channel ?? (isMeeting ? channelDefault : undefined);
      const properties =
        values.properties && typeof values.properties === 'object'
          ? JSON.stringify(values.properties)
          : values.properties;
      const reminderLimit = getReminderLimitForValues(values);
      if (reminderLimit && values.reminderDate && values.reminderDate.isAfter(reminderLimit.maxDate, 'day')) {
        openNotification('error', 'Hata', `Bu statü için görev tarihi en fazla ${reminderLimit.limitDays} gün sonrasına seçilebilir.`);
        return;
      }
      const parsedProperties = (() => {
        if (!properties) return undefined;
        try {
          return typeof properties === 'string' ? JSON.parse(properties) : properties;
        } catch {
          return undefined;
        }
      })();
      const resolvedEditTargetState =
        (editTarget?.state as ReminderStateType) ??
        (typeof editTarget?.status === 'string' ? editTarget.status : undefined);
      const finalState: ReminderStateType =
        (values.state as ReminderStateType) ?? resolvedEditTargetState ?? 'PENDING';
      const finalStatusId =
        values.statusId ??
        editTarget?.statusId ??
        (editTarget?.status && typeof editTarget.status === 'object'
          ? editTarget.status.id
          : undefined);
      const payloadStatusId: number | null | undefined =
        !editTarget && entityName === 'COMPANY' ? null : finalStatusId;
      const payload: ReminderPayload = {
        content: values.content,
        reminderDate,
        dueDate,
        estimatedDurationMinutes: values.estimatedDurationMinutes,
        activityType: values.activityType,
        channel: resolvedChannel,
        link: isMeeting ? values.link : undefined,
        result: values.result,
        tags: normalizeTagNames(values.tags),
        state: finalState,
        ...(payloadStatusId !== undefined ? { statusId: payloadStatusId } : {}),
        ...(properties ? { properties } : {}),
        ...(entityId ? { entityName, entityId } : {}),
        ...(isAdmin && (values.accountId ?? editTarget?.accountId ?? editTarget?.account?.id)
          ? { accountId: values.accountId ?? editTarget?.accountId ?? editTarget?.account?.id }
          : {}),
      };

      const getCleanedProperties = () => {
        if (!parsedProperties || typeof parsedProperties !== 'object') return undefined;
        const { result, resultImages, resultDocuments, ...rest } = parsedProperties as any;
        return Object.keys(rest).length ? JSON.stringify(rest) : undefined;
      };

      const buildDuplicatePayload = (): ReminderPayload => {
        const cleanedProperties = getCleanedProperties();
        const { statusId: removedStatus, ...payloadWithoutStatus } = payload;
        return {
          ...payloadWithoutStatus,
          state: 'PENDING' as ReminderStateType,
          result: undefined,
          properties: cleanedProperties,
        };
      };

      const duplicateExisting = async () => {
        const duplicatePayload = buildDuplicatePayload();
        if (isAdmin) {
          const targetAccountId = values.accountId ?? editTarget?.accountId ?? editTarget?.account?.id;
          await ReminderService.createForAccount({ ...duplicatePayload, accountId: targetAccountId });
        } else {
          await ReminderService.createForMe(duplicatePayload);
        }
        openNotification('success', 'Başarılı', 'Tamamlanan görev temelinde yeni görev oluşturuldu');
      };

      const isEditTargetCompleted = editTarget && editTarget.state === 'COMPLETED';

      if (editTarget) {
        if (isEditTargetCompleted) {
          if (values.duplicateOnSave) {
            await duplicateExisting();
            setDrawerOpen(false);
            setEditTarget(null);
            setDrawerValues({ entityTab: 'COMPANY' });
            fetchReminders();
            return;
          }
          openNotification('error', 'Hata', 'Tamamlanan görev güncellenemez');
          return;
        }

        await ReminderService.updateReminder(editTarget.id, payload);
        openNotification('success', 'Başarılı', 'Görev güncellendi');
        if (finalState === 'COMPLETED' && values.duplicateOnSave) {
          await duplicateExisting();
        }
      } else if (isAdmin) {
        await ReminderService.createForAccount({ ...payload, accountId: values.accountId });
        openNotification('success', 'Başarılı', 'Görev oluşturuldu');
      } else {
        await ReminderService.createForMe(payload);
        openNotification('success', 'Başarılı', 'Görev oluşturuldu');
      }

      setDrawerOpen(false);
      setEditTarget(null);
      setDrawerValues({ entityTab: 'COMPANY' });
      fetchReminders();
    } catch (e: any) {
      if (e?.errorFields) return;
      openNotification('error', 'Hata', e?.response?.data || 'İşlem tamamlanamadı');
    }
  };

  const columns = [
    ...(isAdmin
      ? [
          {
            title: 'Kullanıcı',
            key: 'account',
            render: (_: any, record: ReminderRecord) => renderReminderAccountCell(record),
          },
        ]
      : []),
  
    {
      title: 'Firma',
      key: 'company',
      render: (_: any, record: ReminderRecord) => renderCompanyCell(record),
    },
    {
      title: 'Teklif',
      key: 'proposal',
      render: (_: any, record: ReminderRecord) => renderProposalCell(record),
    },
    {
      title: 'Görev',
      dataIndex: 'content',
      key: 'content',
      render: (_: any, record: ReminderRecord) => {
        const resultValue = getResultValue(record);
        const statusTag = renderReminderStatusBadge(record.status);
        const channelIcon = renderReminderChannelIcon(record.channel);
        const attachments = getReminderAttachments(record);
        return (
          <div>
            {resultValue && (
              <div style={{ color: '#374151', fontSize: 13, marginBottom: 4 }}>
                <b>Sonuç:</b> {resultValue}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              {channelIcon}
              <Tooltip title={record.id ? `#${record.id}` : undefined}>
                <span>{record.content || 'Görev içeriği bulunamadı.'}</span>
              </Tooltip>
            </div>
            {attachments.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                {attachments.map((item, index) => {
                  const Icon = item.type === 'image' ? FileImageOutlined : FileTextOutlined;
                  return (
                    <Tooltip title={item.filename} key={`${item.url}-${index}`}>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: '#374151', display: 'inline-flex', alignItems: 'center' }}
                      >
                        <Icon />
                      </a>
                    </Tooltip>
                  );
                })}
              </div>
            )}
            {statusTag && (
              <div style={{ marginTop: 6 }}>
                {statusTag}
              </div>
            )}
            {getReminderTags(record).length > 0 && (
              <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {getReminderTags(record).map((tagName) => (
                  <Tag key={`${record.id}-${tagName}`} style={{ marginInlineEnd: 0 }}>
                    {tagName}
                  </Tag>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Tarih',
      dataIndex: 'reminderDate',
      key: 'reminderDate',
      render: (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
    },
    {
      title: 'Bitirme Tarihi',
      dataIndex: 'resolvedAt',
      key: 'resolvedAt',
      render: (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
    },
    {
      title: 'Durum',
      dataIndex: 'state',
      key: 'state',
      render: (_: any, record: ReminderRecord) => {
        const stateTag = renderReminderStateTag(record.state);
        if (!stateTag) {
          return '-';
        }
        return (
          <Space direction="vertical" size={2}>
            {stateTag}
          </Space>
        );
      },
    },
      {
      title: 'Oluşturan',
      key: 'createdBy',
      render: (_: any, record: ReminderRecord) => renderCreatedByCell(record),
    },
    {
      title: 'İşlem',
      key: 'actions',
      render: (_: any, record: ReminderRecord) => {
        const authorizedPersonsButton =
          canManageProposalAuthorizedPersons && isProposalReminder(record) ? (
            <Tooltip title="Yetkili Kişiler">
              <Button
                aria-label="Yetkili kişileri aç"
                type="default"
                className="action-button icon-action-button"
                style={{ marginRight: 8 }}
                onClick={() => openAuthorizedPersonsDrawer(record)}
              >
                <UserListIcon style={{ fontSize: 20 }} />
              </Button>
            </Tooltip>
          ) : null;
        const historyButton = isProposalReminder(record) ? (
          <Button type="link" onClick={() => openNotesDrawer(record)}>
            İşlem Geçmişi
          </Button>
        ) : null;

        if (isAdmin) {
          const canComplete = record.state === 'PENDING';
          return (
            <div className="companies-action">
              {authorizedPersonsButton}
              {historyButton}
              {canComplete && (
                <Button type="link" onClick={() => handleComplete(record)}>
                  Tamamla
                </Button>
              )}
              <Button
                icon={<img src={PenIcon} alt="pen" className="icon" />}
                shape="circle"
                onClick={() => handleEdit(record)}
                className="icon-button ml-12px"
              />
              <Popconfirm
                placement="left"
                title="Bu Görevi silmek istediğinize emin misiniz?"
                onConfirm={() => handleDelete(record.id)}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button
                  icon={<img src={TrashIcon} alt="trash" className="icon" />}
                  shape="circle"
                  className="icon-button ml-12px"
                />
              </Popconfirm>
            </div>
          );
        }
        const canCompleteOwn = isOwnedPendingReminder(record);
        if (!authorizedPersonsButton && !historyButton && !canCompleteOwn) return '-';
        return (
          <Space>
            {authorizedPersonsButton}
            {historyButton}
            {canCompleteOwn && (
              <Button type="link" onClick={() => handleComplete(record)}>
                Tamamla
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  const listedCount = pagination.total || data.length;

  return (
      <Card className="br-30px mb-60px">
        <Row justify="space-between" align="middle" className="mb-16px">
          <div>
            <Title style={{ margin: 0, display: 'inline-block' }} level={4}>Görevler</Title>
            <Typography.Text type='secondary' className='ms-5'>
              <strong>({listedCount})</strong> kayıt listelendi
            </Typography.Text>
          </div>
          <Row align="middle">
            <Input
              allowClear
              placeholder="Ara"
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              className="d-input d-input-filter"
              style={{ width: 200, marginRight: 8 }}
              value={searchText}
              onChange={handleSearchChange}
            />
            <Button
              onClick={() => setFilterDrawerOpen(true)}
              type="primary"
              className="d-button width-auto ml-10px dark-button"
            >
              Filtrele
            </Button>
            {canCreateReminder && (
              <Button
                type="primary"
                className="d-button width-auto ml-10px dark-button"
                onClick={handleOpenCreate}
              >
                Yeni Görev Ekle
              </Button>
            )}
          </Row>
        </Row>

        <Space style={{ marginBottom: 12 }}>
          <Segmented
            value={viewMode}
            onChange={(val) => applyViewMode(val as ReminderViewMode)}
            options={[
              { label: 'Geçmiş', value: 'PAST' },
              { label: 'Bugün', value: 'DAILY' },
              { label: 'Sonraki', value: 'FUTURE' },
              { label: 'Bu Hafta', value: 'WEEKLY' },
              { label: 'Bu Ay', value: 'MONTHLY' },
              { label: 'Tümü', value: 'ALL' },
            ]}
          />
        </Space>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={data}
          columns={columns as any}
          pagination={{
            ...pagination,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />

      <ReminderDrawer
        open={drawerOpen}
        isEdit={!!editTarget}
        isAdmin={isAdmin}
        canComplete={canCompleteOwnReminder}
        users={users}
        companies={companies}
        proposals={proposals}
        initialValues={drawerValues}
        onClose={() => {
          setDrawerOpen(false);
          setEditTarget(null);
        }}
        onSubmit={handleDrawerSubmit}
      />
      <CompletionDrawer
        open={completionDrawerVisible}
        onClose={closeCompletionDrawer}
        onSubmit={handleCompletionSubmit}
        submitting={completionSubmitting}
        error={completionError}
        result={completionResult}
        reminder={completionTarget}
        proposal={completionTarget ? resolveProposalEntity(completionTarget) : undefined}
        onResultChange={(value) => {
          setCompletionResult(value);
          if (completionError) setCompletionError(null);
        }}
        switchActive={completionSwitchActive}
        onSwitchChange={(checked) => {
          setCompletionSwitchActive(checked);
          if (!checked) {
            setCompletionError(null);
          }
        }}
        activityType={completionActivityType}
        onActivityTypeChange={(type) => {
          setCompletionActivityType(type);
          if (completionError) setCompletionError(null);
        }}
        channel={completionChannel}
        onChannelChange={(value) => {
          setCompletionChannel(value);
          if (completionError) setCompletionError(null);
        }}
        reminderDate={completionReminderDate}
        onReminderDateChange={(value) => {
          setCompletionReminderDate(value);
          if (completionError) setCompletionError(null);
        }}
        content={completionContent}
        onContentChange={(value) => {
          setCompletionContent(value);
          if (completionError) setCompletionError(null);
        }}
        resultAttachments={completionResultAttachments}
        onResultAttachmentsChange={(files) => setCompletionResultAttachments(files)}
      />

      <EntityNotesDrawer
        open={notesDrawerOpen}
        entityId={notesTargetProposal?.id ?? null}
        entityName="proposal"
        title={notesDrawerTitle}
        proposalStatusId={notesTargetProposal?.statusId ?? notesTargetProposal?.status?.id ?? null}
        proposalStatusName={
          notesTargetProposal
            ? notesTargetProposal?.status?.name || getStatusNameById(notesTargetProposal?.statusId ?? notesTargetProposal?.status?.id) || null
            : null
        }
        proposalStatusColor={
          notesTargetProposal
            ? notesTargetProposal?.status?.color || getStatusColorById(notesTargetProposal?.statusId ?? notesTargetProposal?.status?.id) || null
            : null
        }
        proposalCreatedAt={notesTargetProposal?.createdAt ?? null}
        proposalReferenceId={notesTargetProposal?.referenceId ?? null}
        onClose={closeNotesDrawer}
        onEditReminder={(reminder) => {
          closeNotesDrawer();
          handleEdit(reminder as ReminderRecord);
        }}
      />

      <Drawer
        title="Yetkili Kişiler"
        open={authorizedPersonsDrawerOpen}
        onClose={closeAuthorizedPersonsDrawer}
        width={1000}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button style={{ marginRight: 8 }} onClick={closeAuthorizedPersonsDrawer}>
              İptal
            </Button>
            <Button
              type="primary"
              className="d-button dark-button width-auto"
              onClick={handleSaveAuthorizedPersons}
              disabled={selectedAuthorizedPersonIds.length === 0}
            >
              Kaydet
            </Button>
          </div>
        }
      >
        {authorizedPersonsCompanyId && (
          <AuthorizedPersonTable
            companyId={authorizedPersonsCompanyId}
            showSelection={true}
            selectionFilter="hasAny"
            showSelectionMessage={false}
            selectedAuthorizedPersonIds={selectedAuthorizedPersonIds}
            setSelectedAuthorizedPersonIds={setSelectedAuthorizedPersonIds}
          />
        )}
      </Drawer>

      <Drawer
        title="Filtre"
        placement="right"
        width={360}
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        destroyOnClose
      >
        <Form layout="vertical" form={filterForm} onFinish={handleFilter} initialValues={filterFormInitialValues}>
          <Form.Item label="Durum" name="stateList">
            <Select
              mode="multiple"
              allowClear
              placeholder="Durum seçiniz"
              optionFilterProp="label"
              filterOption={(input, option) =>
                normalizeTurkish(String(option?.label || '')).includes(normalizeTurkish(input))
              }
            >
              {REMINDER_FILTER_STATE_TYPES.map((s: ReminderStateType) => (
                <Select.Option key={s} value={s} label={STATE_LABELS[s] || s}>
                  {STATE_LABELS[s] || s}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Statü" name="filterStatusId">
            <Select
              allowClear
              placeholder="Statü seçiniz"
              optionFilterProp="label"
              filterOption={(input, option) =>
                normalizeTurkish(String(option?.label || '')).includes(normalizeTurkish(input))
              }
            >
              {statuses.map((status) => {
                const label = status?.name || status?.title || status?.id;
                return (
                  <Select.Option key={status?.id ?? label} value={status?.id} label={label}>
                    {label}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item label="Görev Türü" name="activityType">
            <Select allowClear placeholder="Görev Türü seçiniz">
              {ACTIVITY_TYPES.map((s: ActivityType) => (
                <Select.Option key={s} value={s}>
                  {ACTIVITY_LABELS[s] || s}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Etiket" name="filterTagIds">
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="Etiket seçiniz"
              optionFilterProp="label"
              loading={loadingTags}
              notFoundContent={
                tagError ? 'Etiketler yüklenemedi.' : loadingTags ? 'Yükleniyor...' : 'Etiket bulunamadı'
              }
              onSearch={(value) => {
                loadTags(value);
              }}
              filterOption={(input, option) =>
                normalizeTurkish(String(option?.label || '')).includes(normalizeTurkish(input))
              }
            >
              {tagOptions.map((tag) => (
                <Select.Option key={tag.id} value={tag.id} label={tag.name}>
                  {tag.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Süre Tanımı" name="estimatedDurationSpecified">
            <Select allowClear placeholder="Süre tanımı seçiniz">
              <Select.Option value={true}>Evet</Select.Option>
              <Select.Option value={false}>Hayır</Select.Option>
            </Select>
          </Form.Item>
          {isAdmin && (
            <Form.Item label="Kullanıcı" name="filterAccountId">
              <Select
                showSearch
                allowClear
                placeholder="Kullanıcı seçiniz"
                optionFilterProp="label"
                filterOption={(input, option) =>
                  normalizeTurkish(String(option?.label || '')).includes(normalizeTurkish(input))
                }
              >
                {users.map((u) => {
                  const label = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || u.id;
                  return (
                    <Select.Option key={u.id} value={u.id} label={label}>
                      {label}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
          )}
          <Tabs
            activeKey={filters?.entityTab || 'COMPANY'}
            onChange={(key) => {
              const tabKey = key as 'COMPANY' | 'PROPOSAL';
              setFilters((prev: any) => ({
                ...prev,
                entityTab: tabKey,
                entityNames: tabKey === 'COMPANY' ? ['COMPANY'] : ['PROPOSAL'],
                entityId: undefined,
                proposalCompanyIdFilter: undefined,
              }));
              filterForm.setFieldsValue({
                entityTab: tabKey,
                filterCompanyId: undefined,
                filterProposalCompanyId: undefined,
                filterProposalId: undefined,
              });
              filterForm.resetFields(['dateRange']);
            }}
            items={[
              { key: 'COMPANY', label: 'Firma' },
              { key: 'PROPOSAL', label: 'Teklif' },
            ]}
          />

          {((filters?.entityTab || 'COMPANY') as 'COMPANY' | 'PROPOSAL') === 'COMPANY' && (
            <Form.Item label="Firma" name="filterCompanyId">
              <Select
                showSearch
                allowClear
                placeholder="Firma seçiniz"
                optionFilterProp="label"
                filterOption={(input, option) => normalizeTurkish(String(option?.label || '')).includes(normalizeTurkish(input))}
              >
                {companies.map((item: any) => (
                  <Select.Option key={item.id} value={item.id} label={item.name || item.title || item.id}>
                    {item.name || item.title || item.id}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {((filters?.entityTab || 'COMPANY') as 'COMPANY' | 'PROPOSAL') === 'PROPOSAL' && (
            <>
              <Form.Item label="Firma" name="filterProposalCompanyId">
                <Select
                  showSearch
                  allowClear
                  placeholder="Firma seçiniz"
                  optionFilterProp="label"
                  filterOption={(input, option) => normalizeTurkish(String(option?.label || '')).includes(normalizeTurkish(input))}
                  onChange={(val) => {
                    filterForm.setFieldsValue({ filterProposalId: undefined });
                    setFilters((prev: any) => ({ ...prev, proposalCompanyIdFilter: val || undefined }));
                  }}
                >
                  {companies.map((item: any) => (
                    <Select.Option key={item.id} value={item.id} label={item.name || item.title || item.id}>
                      {item.name || item.title || item.id}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="Teklif" name="filterProposalId">
                <Select
                  showSearch
                  allowClear
                  placeholder="Teklif seçiniz"
                  optionFilterProp="label"
                  filterOption={(input, option) => normalizeTurkish(String(option?.label || '')).includes(normalizeTurkish(input))}
                >
                  {proposals
                    .filter((p: any) => {
                      const companyFilter = filters?.proposalCompanyIdFilter;
                      if (!companyFilter) return true;
                      const cid = p.companyId ?? p.company?.id;
                      return String(cid) === String(companyFilter);
                    })
                    .map((item: any) => (
                      <Select.Option
                        key={item.id}
                        value={item.id}
                        label={`${item.proposalNumber || item.proposalCode || item.id} - ${
                          item.createdAt ? dayjs(item.createdAt).format('DD/MM/YYYY') : ''
                        }`}
                      >
                        {`${item.proposalNumber || item.proposalCode || item.id} - ${
                          item.createdAt ? dayjs(item.createdAt).format('DD/MM/YYYY') : ''
                        }`}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </>
          )}

          <Form.Item
            label="Tarih Aralığı"
            name="dateRange"
            extra={viewMode !== 'ALL' && 'Tümü dışındaki görünüm modlarında tarih aralığı gözükmez olur.'}
          >
            {viewMode === 'ALL' && (  <DatePicker.RangePicker
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
            />)}
          
          </Form.Item>
          <Space>
            <Button onClick={handleClearFilters}>Temizle</Button>
            <Button type="primary" htmlType="submit">
              Filtrele
            </Button>
          </Space>
        </Form>
      </Drawer>
    </Card>
  );
};

export default Reminders;
